/**
 * Schedule Service
 * Handles project scheduling and timeline management API calls
 */

import { httpClient, ApiResponse } from './http.client';

export interface Schedule {
  schedule_id: string;
  project_id: string;
  task_name: string;
  description?: string;
  
  // Schedule Details
  start_date: string;
  end_date: string;
  duration_days?: number;
  
  // Dependencies
  predecessor_task_id?: string;
  dependency_type?: string;
  
  // Assignment
  assigned_to?: string;
  estimated_hours?: number;
  actual_hours?: number;
  
  // Status
  status: 'not_started' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  completion_percentage: number;
  priority: 'low' | 'medium' | 'high' | 'critical';
  
  // Metadata
  created_at: string;
  updated_at: string;
}

export interface ScheduleCreate {
  project_id: string;
  task_name: string;
  description?: string;
  start_date: string;
  end_date: string;
  duration_days?: number;
  predecessor_task_id?: string;
  dependency_type?: string;
  assigned_to?: string;
  estimated_hours?: number;
  priority?: 'low' | 'medium' | 'high' | 'critical';
}

export interface ScheduleUpdate {
  task_name?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  duration_days?: number;
  predecessor_task_id?: string;
  dependency_type?: string;
  assigned_to?: string;
  estimated_hours?: number;
  actual_hours?: number;
  status?: 'not_started' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  completion_percentage?: number;
  priority?: 'low' | 'medium' | 'high' | 'critical';
}

export interface ScheduleFilters {
  project_id?: string;
  status?: string;
  priority?: string;
  assigned_to?: string;
  start_date_from?: string;
  start_date_to?: string;
  end_date_from?: string;
  end_date_to?: string;
}

class ScheduleService {
  /**
   * Get all schedule tasks
   */
  async getSchedules(page = 1, perPage = 50, filters?: ScheduleFilters, organizationId?: string): Promise<ApiResponse<{ data: Schedule[]; total: number; page: number; per_page: number }>> {
    const params: any = {
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    };

    if (organizationId) {
      params.organization_id = organizationId;
    }

    const queryParams = new URLSearchParams(params);

    return httpClient.get<{ data: Schedule[]; total: number; page: number; per_page: number }>(
      `/api/v1/schedules/?${queryParams}`
    );
  }

  /**
   * Get schedule tasks for a specific project
   */
  async getProjectSchedule(projectId: string, page = 1, perPage = 100, organizationId?: string): Promise<ApiResponse<{ data: Schedule[]; total: number }>> {
    const params: any = {
      project_id: projectId,
      page: page.toString(),
      per_page: perPage.toString()
    };

    if (organizationId) {
      params.organization_id = organizationId;
    }

    const queryParams = new URLSearchParams(params);

    return httpClient.get<{ data: Schedule[]; total: number }>(
      `/api/v1/schedules/?${queryParams}`
    );
  }

  /**
   * Get single schedule task
   */
  async getSchedule(scheduleId: string): Promise<ApiResponse<Schedule>> {
    return httpClient.get<Schedule>(`/api/v1/schedules/${scheduleId}`);
  }

  /**
   * Create new project schedule
   */
  async createProjectSchedule(scheduleData: {
    organization_id: string;
    project_id: string;
    schedule_name: string;
    version: string;
    status?: string;
    notes?: string;
    start_date?: string;
    end_date?: string;
    is_baseline?: boolean;
  }): Promise<ApiResponse<any>> {
    return httpClient.post('/api/v1/schedules/', scheduleData);
  }

  /**
   * Create new schedule task
   */
  async createSchedule(scheduleData: ScheduleCreate): Promise<ApiResponse<Schedule>> {
    return httpClient.post<Schedule>('/api/v1/schedules/', {
      ...scheduleData,
      status: 'not_started',
      completion_percentage: 0,
      dependency_type: scheduleData.dependency_type || 'finish_to_start'
    });
  }

  /**
   * Update schedule task
   */
  async updateSchedule(scheduleId: string, scheduleData: ScheduleUpdate): Promise<ApiResponse<Schedule>> {
    return httpClient.put<Schedule>(`/api/v1/schedules/${scheduleId}`, scheduleData);
  }

  /**
   * Delete schedule task
   */
  async deleteSchedule(scheduleId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/schedules/${scheduleId}`);
  }

  /**
   * Update task progress
   */
  async updateTaskProgress(scheduleId: string, completionPercentage: number, actualHours?: number): Promise<ApiResponse<Schedule>> {
    const status = completionPercentage === 100 ? 'completed' : 
                   completionPercentage > 0 ? 'in_progress' : 'not_started';

    return httpClient.put<Schedule>(`/api/v1/schedules/${scheduleId}`, {
      completion_percentage: completionPercentage,
      status,
      ...(actualHours !== undefined && { actual_hours: actualHours })
    });
  }

  /**
   * Get project schedule overview/gantt data
   */
  async getProjectScheduleOverview(projectId: string): Promise<ApiResponse<{
    tasks: Schedule[];
    critical_path: string[];
    project_duration: number;
    completion_percentage: number;
  }>> {
    return httpClient.get<{
      tasks: Schedule[];
      critical_path: string[];
      project_duration: number;
      completion_percentage: number;
    }>(`/api/v1/projects/${projectId}/schedule-overview`);
  }

  /**
   * Get schedule by status
   */
  async getSchedulesByStatus(status: string, projectId?: string): Promise<ApiResponse<Schedule[]>> {
    const queryParams = new URLSearchParams({ status });
    if (projectId) {
      queryParams.append('project_id', projectId);
    }
    return httpClient.get<Schedule[]>(`/api/v1/schedules/by-status?${queryParams}`);
  }

  /**
   * Get overdue tasks
   */
  async getOverdueTasks(projectId?: string): Promise<ApiResponse<Schedule[]>> {
    const queryParams = projectId ? `?project_id=${projectId}` : '';
    return httpClient.get<Schedule[]>(`/api/v1/schedules/overdue${queryParams}`);
  }

  /**
   * Get tasks assigned to user
   */
  async getUserTasks(userId: string, projectId?: string): Promise<ApiResponse<Schedule[]>> {
    const queryParams = new URLSearchParams({ assigned_to: userId });
    if (projectId) {
      queryParams.append('project_id', projectId);
    }
    return httpClient.get<Schedule[]>(`/api/v1/schedules/assigned?${queryParams}`);
  }

  /**
   * Bulk update task status
   */
  async bulkUpdateTaskStatus(scheduleIds: string[], status: string): Promise<ApiResponse<void>> {
    return httpClient.post<void>('/api/v1/schedules/bulk-status-update', {
      schedule_ids: scheduleIds,
      status
    });
  }

  /**
   * Get task dependencies
   */
  async getTaskDependencies(scheduleId: string): Promise<ApiResponse<{
    predecessors: Schedule[];
    successors: Schedule[];
  }>> {
    return httpClient.get<{
      predecessors: Schedule[];
      successors: Schedule[];
    }>(`/api/v1/schedules/${scheduleId}/dependencies`);
  }

  /**
   * Create task dependency
   */
  async createTaskDependency(predecessorId: string, successorId: string, dependencyType = 'finish_to_start'): Promise<ApiResponse<void>> {
    return httpClient.post<void>('/api/v1/schedules/dependencies', {
      predecessor_id: predecessorId,
      successor_id: successorId,
      dependency_type: dependencyType
    });
  }

  /**
   * Remove task dependency
   */
  async removeTaskDependency(predecessorId: string, successorId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/schedules/dependencies/${predecessorId}/${successorId}`);
  }

  /**
   * Get schedule analytics
   */
  async getScheduleAnalytics(projectId: string): Promise<ApiResponse<{
    total_tasks: number;
    completed_tasks: number;
    in_progress_tasks: number;
    overdue_tasks: number;
    completion_percentage: number;
    estimated_vs_actual_hours: {
      estimated: number;
      actual: number;
    };
  }>> {
    return httpClient.get<{
      total_tasks: number;
      completed_tasks: number;
      in_progress_tasks: number;
      overdue_tasks: number;
      completion_percentage: number;
      estimated_vs_actual_hours: {
        estimated: number;
        actual: number;
      };
    }>(`/api/v1/projects/${projectId}/schedule-analytics`);
  }

  /**
   * Export schedule to various formats
   */
  async exportSchedule(projectId: string, format: 'pdf' | 'excel' | 'csv' | 'mpp'): Promise<ApiResponse<{ download_url: string }>> {
    return httpClient.post<{ download_url: string }>(`/api/v1/projects/${projectId}/schedule/export`, {
      format
    });
  }

  /**
   * Import schedule from file
   */
  async importSchedule(projectId: string, file: File): Promise<ApiResponse<{ imported_tasks: number; errors: string[] }>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId);

    return httpClient.post<{ imported_tasks: number; errors: string[] }>('/api/v1/schedules/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Create task under a schedule
   */
  async createTask(scheduleId: string, taskData: {
    task_name: string;
    description?: string;
    start_date_planned: string;
    end_date_planned: string;
    duration_days_planned?: number;
    start_date_actual?: string;
    end_date_actual?: string;
    progress_percentage?: string;
    status?: string;
    parent_task_id?: string;
    boq_item_id?: string;
    assigned_to_user_id?: string;
    is_milestone?: boolean;
    critical_path?: boolean;
    organization_id?: string;
  }): Promise<ApiResponse<any>> {
    return httpClient.post(`/api/v1/schedules/${scheduleId}/tasks`, taskData);
  }

  /**
   * Update task
   */
  async updateTask(scheduleId: string, taskId: string, taskData: {
    task_name?: string;
    description?: string;
    start_date_planned?: string;
    end_date_planned?: string;
    duration_days_planned?: number;
    start_date_actual?: string;
    end_date_actual?: string;
    progress_percentage?: string;
    status?: string;
    parent_task_id?: string;
    boq_item_id?: string;
    assigned_to_user_id?: string;
    estimated_hours?: number;
    expected_outcomes?: string;
    additional_notes?: string;
    priority?: string;
    is_milestone?: boolean;
    critical_path?: boolean;
    organization_id?: string;
  }): Promise<ApiResponse<any>> {
    return httpClient.put(`/api/v1/schedules/${scheduleId}/tasks/${taskId}`, taskData);
  }

  /**
   * Get tasks for a schedule
   */
  async getScheduleTasks(scheduleId: string): Promise<ApiResponse<any[]>> {
    return httpClient.get<any[]>(`/api/v1/schedules/${scheduleId}/tasks`);
  }

  /**
   * Create task dependency
   * POST /api/v1/schedules/{schedule_id}/tasks/{task_id}/dependencies
   */
  async createTaskDependencyForTask(
    scheduleId: string,
    taskId: string,
    dependencyData: {
      organization_id: string;
      predecessor_task_id: string;
      dependency_type?: 'Finish to Start' | 'Start to Start' | 'Finish to Finish' | 'Start to Finish';
      lag_days?: number;
    }
  ): Promise<ApiResponse<any>> {
    return httpClient.post(
      `/api/v1/schedules/${scheduleId}/tasks/${taskId}/dependencies`,
      dependencyData
    );
  }

  /**
   * Get task dependencies for a specific task
   */
  async getTaskDependenciesForTask(
    scheduleId: string,
    taskId: string
  ): Promise<ApiResponse<any[]>> {
    return httpClient.get(
      `/api/v1/schedules/${scheduleId}/tasks/${taskId}/dependencies`
    );
  }

  /**
   * Delete task dependency
   */
  async deleteTaskDependency(
    scheduleId: string,
    taskId: string,
    dependencyId: string
  ): Promise<ApiResponse<void>> {
    return httpClient.delete(
      `/api/v1/schedules/${scheduleId}/tasks/${taskId}/dependencies/${dependencyId}`
    );
  }

  /**
   * Start a task
   */
  async startTask(
    scheduleId: string,
    taskId: string,
    data?: {
      notes?: string;
      start_date_actual?: string;
    }
  ): Promise<ApiResponse<any>> {
    return httpClient.post(
      `/api/v1/schedules/${scheduleId}/tasks/${taskId}/start`,
      data || {}
    );
  }

  /**
   * Complete a task
   */
  async completeTask(
    scheduleId: string,
    taskId: string,
    data?: {
      notes?: string;
      end_date_actual?: string;
      completion_notes?: string;
    }
  ): Promise<ApiResponse<any>> {
    return httpClient.post(
      `/api/v1/schedules/${scheduleId}/tasks/${taskId}/complete`,
      data || {}
    );
  }
}


// Export singleton instance
export const scheduleService = new ScheduleService();